import React from 'react';

const defaultOrder = ['personalInfo', 'summary', 'education', 'skills', 'professionalExperience', 'projects', 'certifications', 'awards'];

const ModernTemplate = ({ data, layout, sectionVisibility, sectionOrder, theme }) => {
  const order = sectionOrder || defaultOrder;
  const visibleSections = order.filter(s => sectionVisibility[s] !== false);
  const isTwoColumn = layout === 'two-column';

  const fmtDate = (d) => d || '';

  const SectionTitle = ({ title }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
      <div style={{ width: 4, height: 20, background: `linear-gradient(180deg, ${theme.accentColor || '#06b6d4'}, ${theme.primaryColor || '#1a1a2e'})`, borderRadius: 2 }} />
      <span style={{ fontSize: 18, fontWeight: 700, color: theme.primaryColor || '#1a1a2e', letterSpacing: '-0.3px' }}>{title}</span>
    </div>
  );

  const renderSkills = () => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {data.skills.map((s, i) => (
        <span key={i} style={{
          background: `linear-gradient(135deg, ${theme.accentColor || '#06b6d4'}22, ${theme.primaryColor || '#1a1a2e'}11)`,
          color: theme.accentColor || '#06b6d4',
          padding: '6px 16px',
          borderRadius: 20,
          fontSize: 13,
          fontWeight: 600,
          border: `1px solid ${theme.accentColor || '#06b6d4'}33`
        }}>{s}</span>
      ))}
    </div>
  );

  const renderSection = (key) => {
    switch (key) {
      case 'summary':
        return data.summary ? (
          <div style={{ marginBottom: theme.sectionSpacing || 24 }}>
            <SectionTitle title="About" />
            <div style={{ fontSize: 14, color: '#475569', lineHeight: 1.7 }}>{data.summary}</div>
          </div>
        ) : null;
      case 'education':
        return data.education.length > 0 ? (
          <div style={{ marginBottom: theme.sectionSpacing || 24 }}>
            <SectionTitle title="Education" />
            {data.education.map((edu, i) => (
              <div key={i} style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 4 }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 15, color: theme.primaryColor || '#1a1a2e' }}>{edu.degree}</div>
                    <div style={{ fontSize: 13, color: '#64748b' }}>{edu.school}</div>
                  </div>
                  <div style={{ fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap', background: '#f1f5f9', padding: '2px 10px', borderRadius: 10 }}>
                    {fmtDate(edu.startDate)} - {fmtDate(edu.endDate) || 'Present'}
                  </div>
                </div>
                {edu.description && <div style={{ fontSize: 13, color: '#475569', marginTop: 4 }}>{edu.description}</div>}
              </div>
            ))}
          </div>
        ) : null;
      case 'skills':
        return data.skills.length > 0 ? (
          <div style={{ marginBottom: theme.sectionSpacing || 24 }}>
            <SectionTitle title="Skills" />
            {renderSkills()}
          </div>
        ) : null;
      case 'professionalExperience':
        return data.professionalExperience.length > 0 ? (
          <div style={{ marginBottom: theme.sectionSpacing || 24 }}>
            <SectionTitle title="Experience" />
            {data.professionalExperience.map((exp, i) => (
              <div key={i} style={{ marginBottom: 20, borderLeft: `3px solid ${theme.accentColor || '#06b6d4'}33`, paddingLeft: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 4 }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 15, color: theme.primaryColor || '#1a1a2e' }}>{exp.position}</div>
                    <div style={{ fontSize: 13, color: '#64748b' }}>{exp.company}{exp.location ? `, ${exp.location}` : ''}</div>
                  </div>
                  <div style={{ fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap' }}>
                    {fmtDate(exp.startDate)} - {fmtDate(exp.endDate) || 'Present'}
                  </div>
                </div>
                {exp.description && <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.6, marginTop: 6 }}>{exp.description}</div>}
              </div>
            ))}
          </div>
        ) : null;
      case 'projects':
        return data.projects.length > 0 ? (
          <div style={{ marginBottom: theme.sectionSpacing || 24 }}>
            <SectionTitle title="Projects" />
            {data.projects.map((proj, i) => (
              <div key={i} style={{ marginBottom: 16, background: '#f8fafc', borderRadius: 8, padding: 14, border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 4 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: theme.primaryColor || '#1a1a2e' }}>{proj.title}</div>
                  <div style={{ fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap' }}>
                    {fmtDate(proj.startDate)} - {fmtDate(proj.endDate) || 'Present'}
                  </div>
                </div>
                {proj.description && <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.6, marginTop: 6 }}>{proj.description}</div>}
                {proj.technologies && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
                    {proj.technologies.split(',').map((t, j) => (
                      <span key={j} style={{ fontSize: 11, color: '#64748b', background: '#e2e8f0', padding: '2px 8px', borderRadius: 4 }}>{t.trim()}</span>
                    ))}
                  </div>
                )}
                {proj.link && <a href={proj.link} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: theme.accentColor || '#06b6d4', marginTop: 6, display: 'inline-block', fontWeight: 500 }}>View Project →</a>}
              </div>
            ))}
          </div>
        ) : null;
      case 'certifications':
        return data.certifications.length > 0 ? (
          <div style={{ marginBottom: theme.sectionSpacing || 24 }}>
            <SectionTitle title="Certifications" />
            {data.certifications.map((cert, i) => (
              <div key={i} style={{ marginBottom: 10, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 4 }}>
                <div>
                  <span style={{ fontWeight: 600, fontSize: 14, color: theme.primaryColor || '#1a1a2e' }}>{cert.name}</span>
                  <span style={{ fontSize: 13, color: '#64748b', marginLeft: 8 }}>— {cert.issuer}</span>
                </div>
                {cert.date && <span style={{ fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap' }}>{fmtDate(cert.date)}</span>}
              </div>
            ))}
          </div>
        ) : null;
      case 'awards':
        return data.awards.length > 0 ? (
          <div style={{ marginBottom: theme.sectionSpacing || 24 }}>
            <SectionTitle title="Awards" />
            {data.awards.map((award, i) => (
              <div key={i} style={{ marginBottom: 10, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 4 }}>
                <div>
                  <span style={{ fontWeight: 600, fontSize: 14, color: theme.primaryColor || '#1a1a2e' }}>{award.title}</span>
                  <span style={{ fontSize: 13, color: '#64748b', marginLeft: 8 }}>— {award.issuer}</span>
                </div>
                {award.date && <span style={{ fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap' }}>{fmtDate(award.date)}</span>}
              </div>
            ))}
          </div>
        ) : null;
      default:
        return null;
    }
  };

  const sectionsAfterPersonal = visibleSections.filter(s => s !== 'personalInfo');

  return (
    <div style={{ padding: 40, fontFamily: theme.font || 'Inter, sans-serif', fontSize: theme.fontSize || '14px', lineHeight: theme.lineHeight || 1.6, color: '#334155', maxWidth: 800, margin: '0 auto' }}>
      {/* Header with accent bar */}
      <div style={{
        background: `linear-gradient(135deg, ${theme.primaryColor || '#1a1a2e'}, ${theme.accentColor || '#06b6d4'})`,
        margin: -40, marginBottom: 32,
        padding: '36px 40px',
        color: '#fff'
      }}>
        <h2 style={{ margin: 0, fontSize: 28, fontWeight: 700 }}>{data.personalInfo.fullName || 'Your Name'}</h2>
        {data.personalInfo.title && <div style={{ fontSize: 15, opacity: 0.9, marginTop: 4, fontWeight: 400 }}>{data.personalInfo.title}</div>}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 20px', marginTop: 12, fontSize: 13, opacity: 0.85 }}>
          {data.personalInfo.email && <span>{data.personalInfo.email}</span>}
          {data.personalInfo.phone && <span>{data.personalInfo.phone}</span>}
          {data.personalInfo.address && <span>{data.personalInfo.address}</span>}
          {data.personalInfo.linkedin && <a href={data.personalInfo.linkedin} target="_blank" rel="noopener noreferrer" style={{ color: '#fff', textDecoration: 'underline', opacity: 0.85 }}>LinkedIn</a>}
          {data.personalInfo.github && <a href={data.personalInfo.github} target="_blank" rel="noopener noreferrer" style={{ color: '#fff', textDecoration: 'underline', opacity: 0.85 }}>GitHub</a>}
        </div>
      </div>

      {isTwoColumn ? (
        <div style={{ display: 'flex', gap: 32 }}>
          <div style={{ flex: '0 0 280px' }}>
            {sectionsAfterPersonal.slice(0, Math.ceil(sectionsAfterPersonal.length / 2)).map(s => (
              <div key={s}>{renderSection(s)}</div>
            ))}
          </div>
          <div style={{ flex: 1 }}>
            {sectionsAfterPersonal.slice(Math.ceil(sectionsAfterPersonal.length / 2)).map(s => (
              <div key={s}>{renderSection(s)}</div>
            ))}
          </div>
        </div>
      ) : (
        sectionsAfterPersonal.map(s => <div key={s}>{renderSection(s)}</div>)
      )}
    </div>
  );
};

export default ModernTemplate;
